/**
 * AP Aging Report (v5)
 * 
 * Queries:
 *   1. APBILL (always) - bill headers
 *   2. APBILLITEM (always) - line-item math for balance + retainage held
 *   3. APBILLPAYMENT (historical only) - payment application timeline
 *   4. APRETAINAGERELEASE (always) - release headers (for RELEASEDATE + STATE filter)
 *   5. APRETAINAGERELEASEENTRY (always) - release line amounts
 * 
 * Retainage release timing: Intacct shows releases starting the day AFTER the release date.
 * We match this by using strict less-than: RELEASEDATE < asOfDate.
 * 
 * Excluded states: Reversed, Reversal, Voided, Deleted
 * Skip logic: hide when balance=0 AND net retainage (held - released) <= 0
 */

import {
  toIntacctDate, isDateCurrent, parseDate,
  buildAgingBuckets, buildPaymentLookup, aggregateLineBalances,
  buildRetainageReleaseLookup,
} from './reportHelpers';

const EXCLUDED_STATES = ['Reversed', 'Reversal', 'Voided', 'Deleted'];

const apAgingReport = {
  id: 'ap-aging',
  name: 'AP Aging Report',
  description: 'Outstanding AP balances by vendor with aging buckets. Line-item precision for balances, retainage held/released as separate columns. Historical dates use APBILLPAYMENT for accurate payment timeline.',
  category: 'Accounts Payable',

  parameters: [
    {
      id: 'asOfDate', label: 'As-of Date', type: 'date',
      default: () => new Date().toISOString().split('T')[0],
      description: 'Calculate aging as of this date'
    },
    {
      id: 'agingBuckets', label: 'Aging Periods (days)', type: 'text',
      default: '30,60,90',
      description: 'Comma-separated boundaries (e.g., 30,60,90 → Current, 1-30, 31-60, 61-90, 90+)'
    },
    {
      id: 'vendorFilter', label: 'Vendor ID Filter', type: 'text',
      default: '', description: 'Optional: filter to a specific VENDORID (leave blank for all)'
    },
    {
      id: 'includeFullyPaid', label: 'Include Fully Settled', type: 'checkbox',
      default: false, description: 'Show bills where balance and retainage are both $0'
    }
  ],

  queries: [
    {
      id: 'bills', object: 'APBILL',
      fields: [
        'RECORDNO', 'RECORDID', 'DOCNUMBER', 'VENDORID', 'VENDORNAME',
        'WHENCREATED', 'WHENDUE', 'WHENPAID', 'WHENPOSTED',
        'TOTALENTERED', 'TOTALDUE', 'TOTALPAID', 'TOTALRETAINED',
        'STATE', 'DESCRIPTION', 'CURRENCY',
        'TERMNAME', 'DUE_IN_DAYS', 'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: (params) => {
        const d = toIntacctDate(params.asOfDate);
        let f = `WHENCREATED <= '${d}'`;
        if (params.vendorFilter?.trim()) f += ` AND VENDORID = '${params.vendorFilter.trim()}'`;
        return f;
      }
    },
    {
      id: 'lineItems', object: 'APBILLITEM',
      fields: ['APBILL.RECORDNO', 'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED'],
      buildFilter: (params) => {
        const d = toIntacctDate(params.asOfDate);
        let f = `WHENCREATED <= '${d}'`;
        if (params.vendorFilter?.trim()) f += ` AND VENDORID = '${params.vendorFilter.trim()}'`;
        return f;
      }
    },
    {
      id: 'payments', object: 'APBILLPAYMENT',
      fields: ['RECORDNO', 'RECORDKEY', 'PAYMENTDATE', 'AMOUNT', 'STATE'],
      conditional: (params) => !isDateCurrent(params.asOfDate),
      buildFilter: (params) => `PAYMENTDATE <= '${toIntacctDate(params.asOfDate)}'`
    },
    {
      id: 'retainageHeaders', object: 'APRETAINAGERELEASE',
      fields: ['RECORDNO', 'RELEASEDATE', 'GLPOSTINGDATE', 'STATE'],
      buildFilter: () => 'RECORDNO > 0'  // get all; we filter by date in process
    },
    {
      id: 'retainageEntries', object: 'APRETAINAGERELEASEENTRY',
      fields: ['RECORDNO', 'RRKEY', 'RETAINAGEBILLKEY', 'TRX_AMOUNTRELEASED'],
      buildFilter: () => 'RECORDNO > 0'
    }
  ],

  additionalQueries: [],

  optionalColumns: [
    { id: 'DESCRIPTION2', label: 'Description 2', queryId: 'bills', group: 'Bill Details' },
    { id: 'SUPDOCID', label: 'Attachment ID', queryId: 'bills', group: 'Bill Details' },
    { id: 'BASECURR', label: 'Base Currency', queryId: 'bills', group: 'Bill Details' },
    { id: 'ONHOLD', label: 'On Hold', queryId: 'bills', group: 'Bill Details' },
    { id: 'PAYMENTPRIORITY', label: 'Payment Priority', queryId: 'bills', group: 'Bill Details' },
    { id: 'RECPAYMENTDATE', label: 'Rec. Payment Date', queryId: 'bills', group: 'Bill Details' },
    { id: 'WHENDISCOUNT', label: 'Discount Date', queryId: 'bills', group: 'Bill Details' },
    { id: 'CHECK_NUMBER', label: 'Check Number', queryId: 'bills', group: 'Bill Details' },
    { id: 'PROJECTID', label: 'Project ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'PROJECTNAME', label: 'Project Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'DEPARTMENTID', label: 'Department ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'DEPARTMENTNAME', label: 'Department Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'LOCATIONID', label: 'Location ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'LOCATIONNAME', label: 'Location Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ACCOUNTNO', label: 'Account No', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ACCOUNTTITLE', label: 'Account Title', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CLASSID', label: 'Class ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CLASSNAME', label: 'Class Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CUSTOMERID', label: 'Customer ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'CUSTOMERNAME', label: 'Customer Name', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'EMPLOYEEID', label: 'Employee ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'COSTTYPEID', label: 'Cost Type ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ITEMID', label: 'Item ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'TASKID', label: 'Task ID', queryId: 'lineItems', group: 'Line Item Details' },
    { id: 'ENTRYDESCRIPTION', label: 'Line Description', queryId: 'lineItems', group: 'Line Item Details' },
  ],

  process: (datasets, params, enabledOptionalColumns = [], aggregatedJoins = {}) => {
    const bills = datasets.bills || [];
    const lineItems = datasets.lineItems || [];
    const paymentRecords = datasets.payments || [];
    const retHeaders = datasets.retainageHeaders || [];
    const retEntries = datasets.retainageEntries || [];
    const asOfDate = new Date(params.asOfDate + 'T23:59:59');

    const isCurrentDate = isDateCurrent(params.asOfDate);
    const hasPaymentData = paymentRecords.length > 0;
    const hasLineData = lineItems.length > 0;

    const lineBalances = aggregateLineBalances(lineItems, 'APBILL.RECORDNO');
    const paymentsByBill = hasPaymentData ? buildPaymentLookup(paymentRecords) : {};
    const retainageReleased = buildRetainageReleaseLookup(
      retHeaders, retEntries, 'RETAINAGEBILLKEY', asOfDate
    );

    // Build optional line item aggregated values
    const lineItemOptionals = enabledOptionalColumns.filter(c => c.queryId === 'lineItems');
    const lineItemAggregated = {};
    if (lineItemOptionals.length > 0) {
      for (const line of lineItems) {
        const pk = String(line['APBILL.RECORDNO'] || '').trim();
        if (!pk) continue;
        if (!lineItemAggregated[pk]) lineItemAggregated[pk] = {};
        for (const col of lineItemOptionals) {
          const val = String(line[col.id] || '').trim();
          if (!val) continue;
          if (!lineItemAggregated[pk][col.id]) lineItemAggregated[pk][col.id] = new Set();
          lineItemAggregated[pk][col.id].add(val);
        }
      }
    }

    const { bucketColumns, getBucketIndex } = buildAgingBuckets(params.agingBuckets);
    const billOptionals = enabledOptionalColumns.filter(c => c.queryId === 'bills');

    const reportRows = [];
    let grandTotal = 0, grandRetHeld = 0, grandRetReleased = 0;
    const bucketTotals = new Array(bucketColumns.length).fill(0);
    let skippedPaid = 0, skippedState = 0;

    for (const bill of bills) {
      const totalEntered = parseFloat(bill.TOTALENTERED) || 0;
      const headerRetained = parseFloat(bill.TOTALRETAINED) || 0;
      const state = String(bill.STATE || '').trim();
      const whenDue = parseDate(bill.WHENDUE);
      const rn = String(bill.RECORDNO || '').trim();

      if (EXCLUDED_STATES.includes(state)) { skippedState++; continue; }

      let balanceDue, retainageHeld;
      if (isCurrentDate && hasLineData) {
        // CURRENT: line-item math is most accurate
        const lt = lineBalances[rn];
        if (lt) { balanceDue = lt.balanceDue; retainageHeld = lt.retainageHeld; }
        else { balanceDue = parseFloat(bill.TOTALDUE) || 0; retainageHeld = headerRetained; }
      } else if (hasPaymentData) {
        // HISTORICAL: payment timeline + WHENPAID fallback for credit-applied bills
        const lt = lineBalances[rn];
        retainageHeld = lt ? lt.retainageHeld : headerRetained;

        const whenPaid = parseDate(bill.WHENPAID);
        if (whenPaid && whenPaid <= asOfDate) {
          // Bill was fully settled by as-of date (covers credit applications
          // that don't generate APBILLPAYMENT records)
          balanceDue = 0;
        } else {
          const paidAsOf = paymentsByBill[rn] || 0;
          balanceDue = totalEntered - paidAsOf - retainageHeld;
        }
      } else {
        balanceDue = parseFloat(bill.TOTALDUE) || 0;
        retainageHeld = headerRetained;
      }

      balanceDue = Math.round(balanceDue * 100) / 100;
      retainageHeld = Math.round(retainageHeld * 100) / 100;
      const released = Math.round((retainageReleased[rn] || 0) * 100) / 100;
      const netRetainage = Math.round((retainageHeld - released) * 100) / 100;

      // Skip if balance is 0 AND all retainage is released (net retainage <= 0)
      if (balanceDue === 0 && netRetainage <= 0 && !params.includeFullyPaid) {
        skippedPaid++; continue;
      }

      let daysPastDue = 0;
      if (whenDue && whenDue < asOfDate) daysPastDue = Math.floor((asOfDate - whenDue) / (1000 * 60 * 60 * 24));

      const bucketIdx = getBucketIndex(daysPastDue);
      const ba = new Array(bucketColumns.length).fill(0);
      ba[bucketIdx] = balanceDue;
      bucketTotals[bucketIdx] += balanceDue;
      grandTotal += balanceDue;
      grandRetHeld += retainageHeld;
      grandRetReleased += released;

      const row = {
        'Vendor ID': bill.VENDORID || '',
        'Vendor Name': bill.VENDORNAME || '',
        'Bill Number': bill.RECORDID || '',
        'Document Number': bill.DOCNUMBER || '',
        'Description': bill.DESCRIPTION || '',
        'Bill Date': bill.WHENCREATED || '',
        'Due Date': bill.WHENDUE || '',
        'GL Post Date': bill.WHENPOSTED || '',
        'Terms': bill.TERMNAME || '',
        'Original Amount': totalEntered,
        'Amount Paid': Math.round((totalEntered - balanceDue - retainageHeld) * 100) / 100,
        'Balance Due': balanceDue,
        'Retainage Held': retainageHeld,
        'Retainage Released': released,
        'Days Past Due': daysPastDue,
        'Status': state,
        '_recordUrl': bill.RECORD_URL || '',
        '_entity': bill.MEGAENTITYID || '',
      };

      for (const col of billOptionals) row[col.outputLabel || col.label] = bill[col.id] || '';
      if (lineItemOptionals.length > 0) {
        const agg = lineItemAggregated[rn] || {};
        for (const col of lineItemOptionals) {
          const vals = agg[col.id];
          row[col.outputLabel || col.label] = vals ? [...vals].join(', ') : '';
        }
      }
      bucketColumns.forEach((col, idx) => { row[col] = ba[idx]; });
      reportRows.push(row);
    }

    reportRows.sort((a, b) => {
      const c = (a['Vendor ID'] || '').localeCompare(b['Vendor ID'] || '');
      return c !== 0 ? c : (b['Days Past Due'] || 0) - (a['Days Past Due'] || 0);
    });

    const columns = [
      'Vendor ID', 'Vendor Name', 'Bill Number', 'Document Number', 'Description',
      'Bill Date', 'Due Date', 'GL Post Date', 'Terms',
      'Original Amount', 'Amount Paid', 'Balance Due',
      'Retainage Held', 'Retainage Released',
      'Days Past Due', 'Status',
      ...billOptionals.map(c => c.outputLabel || c.label),
      ...lineItemOptionals.map(c => c.outputLabel || c.label),
      ...bucketColumns
    ];

    const vendorCount = new Set(reportRows.map(r => r['Vendor ID']).filter(Boolean)).size;
    const summaryLines = [
      `As-of Date: ${params.asOfDate}`,
      isCurrentDate ? '(Current — line-item precision)' : hasPaymentData ? '(Historical — APBILLPAYMENT timeline)' : '(Historical — header estimate)',
      `Outstanding: ${reportRows.filter(r => r['Balance Due'] !== 0).length} bills across ${vendorCount} vendor(s)`,
      `Balance Due: $${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Retainage Held: $${grandRetHeld.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Retainage Released: $${grandRetReleased.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      '', ...bucketColumns.map((col, idx) => `${col}: $${bucketTotals[idx].toLocaleString('en-US', { minimumFractionDigits: 2 })}`),
    ];
    if (skippedPaid > 0) summaryLines.push('', `(${skippedPaid} fully-settled bill(s) excluded)`);
    if (skippedState > 0) summaryLines.push(`(${skippedState} reversed/voided bill(s) excluded)`);

    return { columns, data: reportRows, summary: { lines: summaryLines, totals: { grandTotal, grandRetHeld, grandRetReleased, bucketTotals, bucketColumns } } };
  },

  formatRules: {
    'Original Amount': { type: 'currency', decimals: 2 },
    'Amount Paid': { type: 'currency', decimals: 2 },
    'Balance Due': { type: 'currency', decimals: 2 },
    'Retainage Held': { type: 'currency', decimals: 2 },
    'Retainage Released': { type: 'currency', decimals: 2 },
    'Days Past Due': { type: 'number', decimals: 0 },
    'Bill Number': { type: 'hyperlink', urlKey: '_recordUrl', entityKey: '_entity' },
    'Current': { type: 'currency', decimals: 2 },
    '1-30 Days': { type: 'currency', decimals: 2 },
    '31-60 Days': { type: 'currency', decimals: 2 },
    '61-90 Days': { type: 'currency', decimals: 2 },
    '90+ Days': { type: 'currency', decimals: 2 },
  }
};

export default apAgingReport;